import React, { useContext, useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import PageElementsProvider, { PageElementsContext } from '../../../context/pageElementsContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import ResizableColumn from '../DynamicTableDesign/ResizableColumn';
import ResizableRow from '../DynamicTableDesign/ResizableRow';
import DynamicTableDragLayer from '../DynamicTableDesign/DynamicTableDragLayer';
import TextElement from './TextElement';
import { MenuListItem } from '../../../artboard/ContextMenu/styles';
import { DESIGN_ELEMENT_LAYER_DROPDOWN } from '../../../artboard/ContextMenu/translationValues';
import { getRangeOfStaticTable } from '../complexDocumentHelper';
import { FOOTER, HEADER, dummyCallback } from '../../../utils/complexDocumentConstants';
import { constructQuillJSON } from '../../../utils/TextBlockHelper';
import { StaticTable } from '../styles';
import CellOptions from './CellOptions';

const groupRows = (staticTableContents) => {
  const rows = Object.groupBy(staticTableContents, (content) => content.row);
  let rowLength = 1;
  const actualRows = Object.keys(rows).reduce((acc, row) => {
    let rowIndex = parseInt(row);
    let rowsProcessed = 1;
    if (rowIndex === rowLength) {
      acc[row] = rows[row];
    } else {
      let currentIndex = rowLength;
      while (currentIndex < rowIndex) {
        acc[currentIndex] = {};
        currentIndex++;
        rowsProcessed += 1;
      }
      acc[row] = rows[row];
    }
    rowLength += rowsProcessed;
    return acc;
  }, {});
  return actualRows;
}

const NAMESPACES = ['DYNAMIC_TABLE_SETTINGS_SIDEBAR', 'DESIGN_ELEMENT_LAYER_DROPDOWN'];

const CreativeTableEditor = props => {
  const { element, selected, editable, scaling, pageId, adjustScroll, creativeBlock } = props;
  const tableRef = useRef(null);
  const updatingScaling = useRef(false);
  const contentsRef = useRef(element.contents);
  const updatingForStyle = useRef(false);
  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const [ selectedCell, setSelectedCell ] = useState(undefined);
  const [ selectedRange, setSelectedRange ] = useState(undefined);
  const [ showContextMenu, setContextMenu ] = useState(false);
  const [ updateForStyle, setUpdateForStyle ] = useState(false);
  const { t } = useTranslation(NAMESPACES);

  const styleSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
  const [documentStyle] = useDocumentContext(styleSelector, `CreativeTableEditor-${element.uuid}`);
  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    `CreativeTableEditor-${element.uuid}`
  );
  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `CreativeTableEditor-${element.uuid}`);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(previewModeSelector, `CreativeTableEditor-${element.uuid}`);

  const [selectedEditor, setSelectedEditor] = useState(null);

  useEffect(() => {
    contentsRef.current = element;
    if (updatingForStyle.current) {
      updatingForStyle.current = false;
      setUpdateForStyle(false);
    }
  }, [element]);

  useEffect(() => {
    if (!selected) {
      setSelectedEditor(null);
    }
  }, [selected]);

  useEffect(() => {
    if (updateForStyle) {
      // TODO : Get the new row heights
      const finalRowHeight = Array.from(tableRef.current.querySelectorAll('tr')).map(el => el.clientHeight);
      console.log({ finalRowHeight });

      updatingForStyle.current = true;
      updateComplexElementDetails(
        element.uuid,
        {
          element: contentsRef.current,
          rowHeights: finalRowHeight
        },
        'replace-table-element-with-styles'
      );
    }
  }, [updateForStyle]);

  useEffect(() => {
    const handleTableScaling = e => {
      // TODO : Update the new contents of the text in all text editors and remove the replica applied on resizing
      const { action, newContents, scaling } = e.detail;
      updatingScaling.current = true;
      console.log("Table scaling end called!! ", action);
      let customEvent = action !== undefined ? 'handleEditorUndoRedo' : 'updateContentsOnResizeEnd';
      if (scaling === undefined && action === undefined) {
        customEvent = 'resizeEnd';
      }
      newContents.forEach((cell) => {
        const customEventAction = action !== undefined ? {
          action
        } : {
          contents: constructQuillJSON(cell.contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables, pageOverflowEnabled, dataSet),
          scaling
        }
        const refElement = action !== undefined ? document.getElementById(`toolbar-${cell.uuid}`) : tableRef.current.querySelector(`#q-container-${cell.uuid}`);
        if (refElement) {
          refElement.dispatchEvent(new CustomEvent(customEvent, { detail: customEventAction }));
        }
      });
      setTimeout(() => {
        updatingScaling.current = false;
      }, 1000);
    };
    const handleTableStyling = e => {
      const { newElement } = e.detail;
      contentsRef.current = newElement;
      console.log("Setting setUpdateForStyle true!!! ", newElement);
      setUpdateForStyle(true);
    }
    tableRef.current.addEventListener('tableScalingEnd', handleTableScaling);
    tableRef.current.addEventListener('updatingContentsForStyle', handleTableStyling);
    return () => {
      tableRef.current.removeEventListener('tableScalingEnd', handleTableScaling);
      tableRef.current.removeEventListener('updatingContentsForStyle', handleTableStyling);
    }
  }, [updateComplexElementDetails, documentStyle, globalVariables, dataSet, pageOverflowEnabled]);

  const updateDynamicTableContents = useCallback((callbackFn) => {
    if (updatingScaling.current) return;
    const response = callbackFn(contentsRef.current.contents);
    const { elements, ...rest } = response;
    const heightDifference = rest.after.style.height - rest.before.style.height;
    const finalRowHeight = Array.from(tableRef.current.querySelectorAll('tr')).map(el => el.clientHeight);
    console.log({ finalRowHeight: JSON.parse(JSON.stringify(finalRowHeight)) });
    if (heightDifference < 0) {
      const newHeight = finalRowHeight[rest.after.row - 1] + heightDifference;
      finalRowHeight[rest.after.row - 1] = Math.max(finalRowHeight[rest.after.row - 1], newHeight);
    }
    console.log({ finalRowHeight: JSON.parse(JSON.stringify(finalRowHeight)), heightDifference });
    // TODO: Does not work for text undo redo
    updateComplexElementDetails(element.uuid, {
      contents: response.elements,
      style: Object.assign({}, contentsRef.current.style, {
        tableProperties: Object.assign({}, contentsRef.current.style.tableProperties, {
          rowHeights: finalRowHeight
        }),
        height: finalRowHeight.reduce((acc, rowHeight) => acc + rowHeight, 0)
      }),
      nestedChanges: rest
    }, 'contents-with-style');
  }, [updateComplexElementDetails]);

  const onTextElementDoubleClick = useCallback((e) => {
    if (!tableRef.current?.contains(e.target)) return;
    // console.log("cell text element double click called!!");
    e.persist();
    let targetEl = e.target;
    while (targetEl?.tagName !== 'TD') {
      targetEl = targetEl.parentNode;
    }
    const textEditorId = targetEl.id;
    setSelectedEditor(textEditorId);
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, [selected]);

  const onTextElementSingleClick = useCallback((e) => {
    if (!tableRef.current?.contains(e.target)) return;
    e.persist();
    let targetEl = e.target;
    while (targetEl?.tagName !== 'TD') {
      targetEl = targetEl.parentNode;
    }
    setContextMenu(false);
    const textEditorId = targetEl.id;
    if (e.nativeEvent.shiftKey) {
      setSelectedRange(currentRange => {
        if (currentRange?.start) {
          return Object.assign({}, currentRange, {
            end: textEditorId
          })
        }
        return currentRange;
      });
    } else {
      setSelectedEditor(currentEditor => {
        if (textEditorId === currentEditor) return currentEditor;
        setSelectedCell(textEditorId);
        setSelectedRange({
          start: textEditorId
        });
        return null;
      });
    }
  }, []);

  const onColumnResize = useCallback((uuid, columnIndex, differenceInWidth) => {
    let diff = differenceInWidth;
    let index = columnIndex;
    let translate = false;
    if (columnIndex === 'first') {
      diff = differenceInWidth * -1;
      index = 0;
      translate = true;
    }
    updateComplexElementDetails(
      element.uuid,
      {
        index,
        diff,
        translate,
        refElement: tableRef.current,
        prevent: () => {
          message.warn(t(DESIGN_ELEMENT_LAYER_DROPDOWN.RESIZE_COLUMN_WARN.key, DESIGN_ELEMENT_LAYER_DROPDOWN.RESIZE_COLUMN_WARN.value));
        }
      },
      'static-table-resize-column'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, []);

  const onRowResize = useCallback((uuid, rowIndex, differenceInHeight) => {
    let diff = differenceInHeight;
    let index = rowIndex;
    let translate = false;
    if (rowIndex === 'first') {
      diff = differenceInHeight * -1;
      index = 0;
      translate = true;
    }

    updateComplexElementDetails(
      element.uuid,
      {
        index,
        diff,
        translate,
        refElement: tableRef.current
      },
      'static-table-resize-row'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, []);

  const onAddColumn = useCallback((e) => {
    const isLeft = e.currentTarget.id.includes('first');
    let columnIndex = 0;
    if (!isLeft) {
      columnIndex = parseInt(e.currentTarget.id.replaceAll(`add-col-${element.uuid}-`, ''), 10);
    }
    updateComplexElementDetails(
      element.uuid,
      {
        index: columnIndex,
        isLeft
      },
      'static-table-add-column'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, [element.uuid]);

  const onAddRow = useCallback((e) => {
    const isTop = e.currentTarget.id.includes('first');
    let rowIndex = 0;
    if (!isTop) {
      rowIndex = parseInt(e.currentTarget.id.replaceAll(`add-row-${element.uuid}-`, ''), 10);
    }
    updateComplexElementDetails(
      element.uuid,
      {
        index: rowIndex,
        isTop
      },
      'static-table-add-row'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, []);

  const onDeleteColumns = useCallback((e) => {
    setSelectedRange((selectedRange) => {
      updateComplexElementDetails(
        element.uuid,
        {
          selectedRange,
          prevent: () => {
            message.warn(t(DESIGN_ELEMENT_LAYER_DROPDOWN.DELETE_ALL_COLUMN_WARN.key, DESIGN_ELEMENT_LAYER_DROPDOWN.DELETE_ALL_COLUMN_WARN.value));
          },
          refElement: tableRef.current
        },
        'static-table-delete-columns'
      );
      setSelectedCell(undefined);
      return undefined;
    })
  }, []);

  const onDeleteRows = useCallback((e) => {
    setSelectedRange((selectedRange) => {
      updateComplexElementDetails(
        element.uuid,
        {
          selectedRange,
          prevent: () => {
            message.warn(t(DESIGN_ELEMENT_LAYER_DROPDOWN.DELETE_ALL_ROW_WARN.key, DESIGN_ELEMENT_LAYER_DROPDOWN.DELETE_ALL_ROW_WARN.value));
          },
          refElement: tableRef.current
        },
        'static-table-delete-rows'
      );
      setSelectedCell(undefined);
      return undefined;
    })
  }, []);

  const onMergeCells = useCallback(() => {
    setSelectedRange((selectedRange) => {
      updateComplexElementDetails(
        element.uuid,
        {
          selectedRange
        },
        'static-table-merge-cells'
      );
      setSelectedCell(undefined);
      return undefined;
    })
  }, []);

  const onSplitCells = useCallback(() => {
    setSelectedRange((selectedRange) => {
      updateComplexElementDetails(
        element.uuid,
        {
          selectedRange,
          refElement: tableRef.current
        },
        'static-table-split-cells'
      );
      setSelectedCell(undefined);
      return undefined;
    })
  }, []);

  const onRightClick = useCallback((e) => {
    setContextMenu(true);
  }, []);

  let topStart = 0;
  let rangeCellsDimensions = undefined;
  const tableElement = updateForStyle ? contentsRef.current : element;

  const rowContents = groupRows(tableElement.contents);

  const startCell = selectedRange?.start ? tableElement.contents.find(cell => cell.uuid === selectedRange.start) : undefined;
  let rowStartIndex = startCell?.row - 1, colStartIndex = startCell?.column - 1;
  if (selected && selectedRange?.end) {
    const endCell = tableElement.contents.find(cell => cell.uuid === selectedRange.end);
    const {
      rowStartIndex: mergeRowStart,
      rowEndIndex,
      colStartIndex: mergeColStart,
      colEndIndex,
    } = getRangeOfStaticTable(tableElement, selectedRange, true);
    rowStartIndex = mergeRowStart;
    colStartIndex = mergeColStart;

    rangeCellsDimensions = {
      width: tableElement.style.tableProperties.columnWidths.reduce((acc, w, wIndex) => {
        if (wIndex >= colStartIndex && wIndex <= colEndIndex) {
          return acc + w;
        }
        return acc;
      }, 0),
      height: tableElement.style.tableProperties.rowHeights.reduce((acc, h, hIndex) => {
        if (hIndex >= rowStartIndex && hIndex <= rowEndIndex) {
          return acc + h;
        }
        return acc;
      }, 0)
    }
    console.log({ rangeCellsDimensions });
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        let maxRow = 0;
        let maxColumn = 0;
        for (let cell of tableElement.contents) {
          maxRow = Math.max(maxRow, cell.row + cell.rowSpan - 1);
          maxColumn = Math.max(maxColumn, cell.column + cell.columnSpan - 1);
        }
        const firstCell = tableElement.contents.find(cell => cell.row === 1 && cell.column === 1);
        let lastCell;
        for (let i = tableElement.contents.length - 1; i >= 0; i--) {
          const cell = tableElement.contents[i];
          if (cell.row + cell.rowSpan - 1 === maxRow && cell.column + cell.columnSpan - 1 === maxColumn) {
            lastCell = cell;
            break;
          }
        }
        setSelectedRange({
          start: firstCell.uuid,
          end: lastCell.uuid
        });
      }
    };
    if(selected && selectedEditor === null) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selected, selectedEditor, tableElement]);

  return (
    <PageElementsProvider isEmail={true} isComplexDesign={true} parentElements={element.contents} setParentElements={updateDynamicTableContents}>
    <>
      <StaticTable id={element.uuid} ref={tableRef} border={element.style.border} onContextMenu={onRightClick}>
        <colgroup>
          {tableElement.style.tableProperties.columnWidths.map((colWidth, headerIndex) => {
            return (
              <col style={{ width: `${colWidth}px`}} />
            )
          })}
        </colgroup>
        {Object.values(rowContents).map((cells, rowIndex) => {
          if (Object.keys(cells).length === 0) return (<tr></tr>);
          let cellRight = 0;
          const orderedCells = cells.sort((a, b) => {
            if (a.column > b.column) {
              return 1;
            } else if (a.column < b.column) {
              return -1;
            }
            return 0;
          });
          return (
            <tr>
              {orderedCells.map((cellContent, cellIndex) => {
                const rowHeight = cellContent.rowSpan > 1 ? tableElement.style.tableProperties.rowHeights.reduce((acc, rowHeight, rowHeightIndex) => {
                  if (rowHeightIndex >= cellContent.row - 1 && rowHeightIndex < cellContent.row + cellContent.rowSpan - 1) {
                    return acc + rowHeight;
                  }
                  return acc;
                }, 0) : tableElement.style.tableProperties.rowHeights[cellContent.row - 1];
                return (
                  <>
                  <td
                    id={cellContent.uuid}
                    rowspan={cellContent.rowSpan}
                    colspan={cellContent.columnSpan}
                    key={cellContent.uuid}
                    style={{
                      width: tableElement.style.tableProperties.columnWidths[cellContent.column - 1],
                      height: rowHeight,
                      borderColor: cellContent.style.border?.borderColor || 'black',
                      borderStyle: 'solid',
                      borderTopWidth: cellContent.style.border?.top ? '1px' : 0,
                      borderRightWidth: cellContent.style.border?.right ? '1px' : 0,
                      borderBottomWidth: cellContent.style.border?.bottom ? '1px' : 0,
                      borderLeftWidth: cellContent.style.border?.left ? '1px' : 0,
                      backgroundColor: cellContent.style.backgroundColor || 'transparent',
                      padding: `${cellContent.style.padding.top}px ${cellContent.style.padding.right}px ${cellContent.style.padding.bottom}px ${cellContent.style.padding.left}px`,
                      position: 'relative',
                      verticalAlign: cellContent.style.verticalAlignment === 'before' ? 'top' : cellContent.style.verticalAlignment === 'after' ? 'bottom' : 'middle',
                  }}
                    onClick={selected ? onTextElementSingleClick : dummyCallback}
                    onDoubleClick={selected ? onTextElementDoubleClick : dummyCallback}
                  >
                    {(cellContent.contents[0]?.content !== '' || (selected && selectedEditor === cellContent.uuid)) && <TextElement // Reusing existing component since additional styling values of x, y will not change with text
                      element={cellContent}
                      editable={selected && editable}
                      scaling={scaling}
                      selected={selected && selectedEditor === cellContent.uuid}
                      pageId={pageId}
                      adjustScroll={adjustScroll}
                      isFromCreativeBlock={false}
                      key={cellContent.uuid}
                      isStandalone= {creativeBlock?.isStandalone}
                      isHeaderEelement = {creativeBlock?.type === HEADER || creativeBlock?.type === FOOTER}
                      isCreativeStaticBlock={true}
                    />}
                    {selected && selectedCell === cellContent.uuid && (
                      <div
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          outline: '1px dashed #0de0e9e6',
                          pointerEvents: 'none',
                          top: 0,
                          left: 0
                        }}
                      />
                    )}
                    {selected && rowStartIndex + 1 === cellContent.row && colStartIndex + 1 === cellContent.column
                      && selectedRange?.end && (
                      <div
                        style={{
                          position: 'absolute',
                          width: `${rangeCellsDimensions?.width}px`,
                          height: `${rangeCellsDimensions?.height}px`,
                          backgroundColor: '#0de0e926',
                          pointerEvents: 'none',
                          zIndex: 1,
                          left: 0,
                          top: 0
                        }}
                      />
                    )}
                  </td>
                  </>
                )
              })}
              {selected && rowIndex === 0 &&
                <>
                    <ResizableColumn
                      itemUUID={element.uuid}
                      uuid={`col-${element.uuid}-first`}
                      columnIndex={"first"}
                      onResize={onColumnResize}
                      left={0}
                      leftStart={cellRight}
                      scaling={scaling}
                      isStatic={true}
                      customClassName={'col-resizer'}
                      onAddColumn={onAddColumn}
                    />
                  {tableElement.style.tableProperties.columnWidths.map((colWidth, columnIndex) => {
                    cellRight += colWidth;
                    return (
                    <ResizableColumn
                      itemUUID={element.uuid}
                      uuid={`col-${element.uuid}-columnIndex`}
                      key={`col-${element.uuid}-columnIndex`}
                      columnIndex={columnIndex}
                      onResize={onColumnResize}
                      left={0}
                      leftStart={cellRight}
                      scaling={scaling}
                      isStatic={true}
                      customClassName={'col-resizer'}
                      onAddColumn={onAddColumn}
                    />
                  )})}
              </>
              }
            </tr>
          )
        })}
        {editable && selected &&
          <>
            <ResizableRow
              itemUUID={element.uuid}
              uuid={`${element.uuid}-first`}
              rowIndex={"first"}
              onResize={onRowResize}
              left={0}
              topStart={topStart}
              scaling={scaling}
              isStatic={true}
              customClassName={'row-resizer'}
              onAddRow={onAddRow}
            />
            {tableElement.style.tableProperties.rowHeights.map((rowHeight, rowIndex) => {
              topStart += rowHeight;
              return (
                <ResizableRow
                  itemUUID={element.uuid}
                  uuid={`${element.uuid}-${rowIndex}`}
                  key={rowIndex}
                  rowIndex={rowIndex}
                  onResize={onRowResize}
                  left={0}
                  topStart={topStart}
                  scaling={scaling}
                  isStatic={true}
                  customClassName={'row-resizer'}
                  onAddRow={onAddRow}
                />
              )
            })}
          </>
        }
        {editable && selected && <DynamicTableDragLayer scaling={scaling} tableRef={tableRef} tableUUID={element.uuid} />}
      </StaticTable>
      {showContextMenu && document.getElementById('extraMenuItems') && ReactDOM.createPortal(
        <>
        {selectedRange?.end && (<MenuListItem
          onClick={onMergeCells}
        >
          {t(
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_MERGE.key,
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_MERGE.value,
          )}
        </MenuListItem>)}
        {(startCell?.rowSpan > 1 || startCell?.columnSpan > 1) && (<MenuListItem
          onClick={onSplitCells}
        >
          {t(
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_UNMERGE.key,
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_UNMERGE.value,
          )}
        </MenuListItem>)}
        {startCell && (<MenuListItem
          onClick={onDeleteRows}
        >
          {t(
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_DELETE_ROW.key,
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_DELETE_ROW.value,
          )}
        </MenuListItem>)}
        {startCell && (<MenuListItem
          onClick={onDeleteColumns}
        >
          {t(
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_DELETE_COLUMN.key,
            DESIGN_ELEMENT_LAYER_DROPDOWN.STATIC_TABLE_DELETE_COLUMN.value,
          )}
        </MenuListItem>)}
        </>,
        document.getElementById('extraMenuItems')
      )}

      {selected && editable && selectedRange?.start && ReactDOM.createPortal(
        (
          <CellOptions
            selectedRange={selectedRange}
            element={element}
            updateComplexElementDetails={updateComplexElementDetails}
            tableRef={tableRef}
            flowUUID={pageId.slice(0, -2)}
            creativeBlockUUID={creativeBlock.uuid}
          />
        ),
        document.getElementById('document-editor-toolbar'),
      )}
    </>
    </PageElementsProvider>
  )
}

export default CreativeTableEditor;